import {
  endOfMonth,
  endOfYear,
  format,
  parseISO,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { css } from 'react-strict-dom';

import { Dropdown, DropdownItem, Text } from '../../components';
import { formatAsDate } from '../../util';

// How far back "All time" reaches.
const EARLIEST_DATE = '2018-06-01';

type Props = Readonly<{
  fromDate: string;
  toDate: string;
  setFromDate: (arg1: string) => unknown;
  setToDate: (arg1: string) => unknown;
}>;

export default function RangeChoices({
  fromDate,
  toDate,
  setFromDate,
  setToDate,
}: Props) {
  const rangeChoices: Array<[string, Date, Date]> = [
    ['Last 30 days', subDays(new Date(), 30), new Date()],
    ['Last 90 days', subDays(new Date(), 90), new Date()],
    ['This month', startOfMonth(new Date()), new Date()],
    [
      'Last month',
      startOfMonth(subMonths(new Date(), 1)),
      endOfMonth(subMonths(new Date(), 1)),
    ],
    [
      'Last 3 months',
      startOfMonth(subMonths(new Date(), 4)),
      endOfMonth(subMonths(new Date(), 1)),
    ],
    [
      'Last 6 months',
      startOfMonth(subMonths(new Date(), 7)),
      endOfMonth(subMonths(new Date(), 1)),
    ],
    [
      'Last year',
      startOfYear(subYears(new Date(), 1)),
      endOfYear(subYears(new Date(), 1)),
    ],
    ['All time', parseISO(EARLIEST_DATE), new Date()],
  ];

  return (
    <Dropdown
      anchorSide="right"
      trigger={
        <Text as="p" style={styles.rangePrompt}>
          <Text style={styles.rangePromptSpan}>
            {format(parseISO(fromDate), 'd MMM yyyy')}
          </Text>{' '}
          &mdash;{' '}
          <Text style={styles.rangePromptSpan}>
            {format(parseISO(toDate), 'd MMM yyyy')}
          </Text>
        </Text>
      }
    >
      {rangeChoices.map((choice) => (
        <DropdownItem
          key={choice[0]}
          name={
            <Text style={styles.rangeChoice}>
              {choice[0]}{' '}
              <Text style={styles.rangeChoiceSpan}>
                {format(choice[1], 'd MMM yyyy')} &mdash;{' '}
                {format(choice[2], 'd MMM yyyy')}
              </Text>
            </Text>
          }
          onClick={() => {
            setFromDate(formatAsDate(choice[1]));
            setToDate(formatAsDate(choice[2]));
          }}
        />
      ))}
    </Dropdown>
  );
}

const styles = css.create({
  rangePrompt: {
    backgroundColor: '#fff',
    borderRadius: 5,
    boxShadow: '0 2px 5px 0 rgba(51, 51, 79, 0.07)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#eee',
    paddingBlock: 12,
    paddingInline: 20,
    color: '#aaa',
    transition: 'all 0.25s',
    ':hover': {
      borderColor: '#5742e455',
    },
  },
  rangePromptSpan: {
    color: '#555',
  },
  rangeChoice: {
    color: '#5742e4',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  rangeChoiceSpan: {
    fontWeight: '400',
    fontSize: 12,
    color: '#999',
  },
});
